import { useCallback, useEffect, useRef, useState } from 'react';
import type { SnapMatchEvent, ViewportInfo } from '../../hooks/useFabricCanvas';
import type { Guide } from '../../types/image-editor.types';

/** Pixel thickness of the ruler chrome — kept in sync with the
 *  constant in `Rulers.tsx`. Used to offset guide rendering when
 *  rulers are visible AND to detect "drag back to ruler = delete". */
const RULER_THICKNESS = 22;

/** Hit-radius in CSS pixels for grabbing a guide. Tighter than
 *  the visual line (1px) so two close guides don't compete for
 *  the same click. 6px is comfortable for both mouse and trackpad. */
const HIT_RADIUS = 6;

interface GuidesProps {
  viewport: ViewportInfo;
  guides: Guide[];
  rulersVisible: boolean;
  lockGuides: boolean;
  /** Fired when a guide drag commits to a new position. The
   *  parent dispatches MOVE_GUIDE. */
  onMoveGuide: (id: string, position: number) => void;
  /** Fired when a guide is dragged back onto the ruler chrome
   *  (or off the canvas entirely) — the parent dispatches
   *  REMOVE_GUIDE. */
  onRemoveGuide: (id: string) => void;
  /** Subscribe to snap-match events from the canvas hook. Used
   *  to briefly flash the matched guide in magenta — so the
   *  user sees WHICH guide caused the snap and gains confidence
   *  that the alignment is locked. */
  subscribeToSnapMatch?: (cb: (m: SnapMatchEvent) => void) => () => void;
}

/** How long a snap-match flash highlight lingers (ms). Long
 *  enough to be visible during a typical drag tick but short
 *  enough to fade out when the drag releases the snap. */
const SNAP_FLASH_MS = 300;

/** SVG-based guide overlay. Renders each guide as a 1px cyan
 *  line spanning the viewport. Lines are hit-testable for
 *  interactive moves (when `lockGuides` is false); dragging a
 *  guide onto the ruler chrome (or off the wrapper) removes it.
 *
 *  Lives ABOVE the Fabric canvas in z-order so hit-tests beat
 *  Fabric's own (which would otherwise see the click as
 *  "deselect / start marquee"). pointer-events is enabled on
 *  the SVG only when there's at least one guide to interact
 *  with — without a guide the layer is invisible to the cursor. */
export function Guides({
  viewport,
  guides,
  rulersVisible,
  lockGuides,
  onMoveGuide,
  onRemoveGuide,
  subscribeToSnapMatch,
}: GuidesProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  // Highlighted-guide ids — flashes the matched guide(s) in
  // magenta for SNAP_FLASH_MS ms each time the canvas reports a
  // snap match. Refreshed on every snap tick so a sustained drag
  // along a guide keeps it lit continuously.
  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(() => new Set());
  const flashTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!subscribeToSnapMatch) return;
    return subscribeToSnapMatch((m) => {
      const ids: string[] = [];
      if (m.matchedX && m.matchedX !== 'grid') ids.push(m.matchedX);
      if (m.matchedY && m.matchedY !== 'grid') ids.push(m.matchedY);
      if (ids.length === 0) return;
      setHighlightedIds(new Set(ids));
      if (flashTimerRef.current !== null) window.clearTimeout(flashTimerRef.current);
      flashTimerRef.current = window.setTimeout(() => {
        setHighlightedIds(new Set());
        flashTimerRef.current = null;
      }, SNAP_FLASH_MS);
    });
  }, [subscribeToSnapMatch]);
  useEffect(() => {
    return () => {
      if (flashTimerRef.current !== null) window.clearTimeout(flashTimerRef.current);
    };
  }, []);

  const [dragging, setDragging] = useState<{
    id: string;
    axis: 'horizontal' | 'vertical';
    /** Live screen-pixel position during the drag. Committed to
     *  scene-space on mouseup. */
    screenPos: number;
    /** Full cursor position in SVG-local coords. Drives the
     *  "Release to delete" badge near the cursor when in the
     *  delete zone — without both axes we can't anchor the badge. */
    cursorLocalX: number;
    cursorLocalY: number;
    /** When true, the cursor is currently off-canvas / over the
     *  ruler chrome — release here = delete. The overlay also
     *  hides the guide visually while in this state. */
    pendingDelete: boolean;
  } | null>(null);

  const offset = rulersVisible ? RULER_THICKNESS : 0;
  const w = Math.max(0, viewport.viewportWidth - offset);
  const h = Math.max(0, viewport.viewportHeight - offset);

  /** Convert scene-space coord to screen-pixel inside this SVG
   *  (which is itself offset by `offset` from the wrapper). */
  const sceneToLocal = useCallback(
    (axis: 'horizontal' | 'vertical', scene: number): number => {
      if (axis === 'horizontal') {
        return scene * viewport.zoom + viewport.panY - offset;
      }
      return scene * viewport.zoom + viewport.panX - offset;
    },
    [viewport.zoom, viewport.panX, viewport.panY, offset],
  );

  /** Convert local screen-pixel inside the SVG back to scene
   *  coords. Used at commit time. */
  const localToScene = useCallback(
    (axis: 'horizontal' | 'vertical', local: number): number => {
      if (viewport.zoom <= 0) return 0;
      if (axis === 'horizontal') return (local + offset - viewport.panY) / viewport.zoom;
      return (local + offset - viewport.panX) / viewport.zoom;
    },
    [viewport.zoom, viewport.panX, viewport.panY, offset],
  );

  // Window-level drag listeners — capture moves once the cursor
  // leaves the SVG bounds. Symmetric to the Rulers' drag-to-spawn
  // pattern.
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const localX = e.clientX - rect.left;
      const localY = e.clientY - rect.top;
      // pendingDelete = cursor over ruler chrome on the
      // OPPOSITE side from the guide's axis (horizontal guide
      // dragged into the top ruler; vertical guide dragged into
      // the left ruler) OR cursor outside the wrapper entirely.
      const wrapperRect = svg.parentElement?.getBoundingClientRect();
      const offWrapper =
        !wrapperRect ||
        e.clientX < wrapperRect.left ||
        e.clientX > wrapperRect.right ||
        e.clientY < wrapperRect.top ||
        e.clientY > wrapperRect.bottom;
      const overRuler =
        rulersVisible &&
        ((dragging.axis === 'horizontal' &&
          e.clientY - (wrapperRect?.top ?? 0) < RULER_THICKNESS) ||
          (dragging.axis === 'vertical' && e.clientX - (wrapperRect?.left ?? 0) < RULER_THICKNESS));
      setDragging((prev) =>
        prev
          ? {
              ...prev,
              screenPos: dragging.axis === 'horizontal' ? localY : localX,
              cursorLocalX: localX,
              cursorLocalY: localY,
              pendingDelete: offWrapper || overRuler,
            }
          : prev,
      );
    };
    const onUp = () => {
      const cur = dragging;
      if (!cur) return;
      if (cur.pendingDelete) {
        onRemoveGuide(cur.id);
      } else {
        const scene = localToScene(cur.axis, cur.screenPos);
        onMoveGuide(cur.id, scene);
      }
      setDragging(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, rulersVisible, onMoveGuide, onRemoveGuide, localToScene]);

  const beginDrag = useCallback(
    (g: Guide, screenPos: number, cursorLocalX: number, cursorLocalY: number) => {
      if (lockGuides) return;
      setDragging({
        id: g.id,
        axis: g.axis,
        screenPos,
        cursorLocalX,
        cursorLocalY,
        pendingDelete: false,
      });
    },
    [lockGuides],
  );

  // When `guides` is empty AND nothing is being dragged, the
  // overlay disables pointer events so the Fabric canvas
  // underneath is fully reachable. When a guide exists, the
  // overlay enables pointer events but each guide line uses a
  // small hit zone (the visible line is 1px; the hit zone is
  // ±HIT_RADIUS via a wider transparent line layered behind).
  const interactive = !lockGuides && guides.length > 0;

  return (
    <svg
      ref={svgRef}
      width={w}
      height={h}
      className="absolute z-[15] select-none"
      style={{
        left: offset,
        top: offset,
        width: w,
        height: h,
        pointerEvents: interactive ? 'none' : 'none',
      }}
      aria-hidden="true"
    >
      {guides.map((g) => {
        const isDragged = dragging?.id === g.id;
        const localPos = isDragged ? dragging.screenPos : sceneToLocal(g.axis, g.position);
        const visible =
          localPos >= 0 &&
          localPos <= (g.axis === 'horizontal' ? h : w) &&
          !(isDragged && dragging.pendingDelete);
        if (!visible) return null;
        const isHorizontal = g.axis === 'horizontal';
        const lineProps = isHorizontal
          ? { x1: 0, y1: localPos + 0.5, x2: w, y2: localPos + 0.5 }
          : { x1: localPos + 0.5, y1: 0, x2: localPos + 0.5, y2: h };
        const hitProps = isHorizontal
          ? { x1: 0, y1: localPos, x2: w, y2: localPos }
          : { x1: localPos, y1: 0, x2: localPos, y2: h };
        return (
          <g
            key={g.id}
            style={{
              pointerEvents: interactive ? 'auto' : 'none',
              cursor: isHorizontal ? 'ns-resize' : 'ew-resize',
            }}
            onMouseDown={(e) => {
              if (lockGuides) return;
              const svg = svgRef.current;
              if (!svg) return;
              const rect = svg.getBoundingClientRect();
              const localX = e.clientX - rect.left;
              const localY = e.clientY - rect.top;
              const local = isHorizontal ? localY : localX;
              beginDrag(g, local, localX, localY);
            }}
          >
            {/* Transparent wider hit zone */}
            <line {...hitProps} stroke="transparent" strokeWidth={HIT_RADIUS * 2} />
            {/* Visible guide line. Switches to magenta + 2px
                while the snap engine reports this guide as
                matched — visual feedback that the dragged
                layer locked to THIS specific guide. */}
            <line
              {...lineProps}
              stroke={highlightedIds.has(g.id) ? '#ec4899' : '#06b6d4'}
              strokeWidth={highlightedIds.has(g.id) ? 2 : 1}
              shapeRendering="crispEdges"
            />
          </g>
        );
      })}

      {/* "Release to delete" affordance — appears mid-drag when
          the cursor strays onto the ruler chrome or off the
          wrapper. Without this the drag-off-to-delete gesture is
          invisible (you only learn it exists after accidentally
          deleting a guide). The visual hides the guide line
          itself (`visible` filter above) and shows this badge in
          its place. Pointer events disabled — never intercepts
          the in-flight drag. */}
      {dragging?.pendingDelete && (
        <g pointerEvents="none">
          <rect
            x={dragging.cursorLocalX + 12}
            y={dragging.cursorLocalY + 12}
            width={108}
            height={22}
            rx={4}
            fill="#dc2626"
            fillOpacity={0.92}
          />
          <text
            x={dragging.cursorLocalX + 18}
            y={dragging.cursorLocalY + 27}
            fill="white"
            fontSize={11}
            fontFamily="ui-sans-serif, system-ui"
            fontWeight={500}
          >
            Release to delete
          </text>
        </g>
      )}
    </svg>
  );
}
